import React, { useEffect, useState, Fragment, type FunctionComponent } from 'react'
import styled from '@emotion/styled'
import {
  fontSize,
  defaultLineHeight,
  colors,
  spacing,
  Select,
} from '@retailer-platform/shared-components'
import { Checkbox } from '@retailer-platform/shared-components/src/common/checkbox/Checkbox'
import { type OptionProps } from 'react-select'
import {
  type SelectProps,
  type SimpleOption,
} from '@retailer-platform/shared-components/src/common/select/utils/select.types'
import partition from 'lodash/partition'
import { type UseFormReturn } from 'react-hook-form-7'
import { useDomainMessages } from 'domains/account-management/utils/domain/intl'
import { useAccountManagementRoles } from 'domains/account-management/components/role-select/utils/AccountManagementRoleSelect.hooks'
import { type FormData } from 'domains/account-management/components/account-form/utils/AccountForm.types'
import { idsSelectStyles } from 'domains/account-management/styles/accountManagement.styles'
import { ErrorMessage } from 'gin-and-tonic/common/error-message/ErrorMessage'
import { type OptionType } from './utils/AccountManagementRoleSelect.types'

export interface Props<TValue>
  extends Omit<SelectProps<SimpleOption<TValue>>, 'options' | 'isMulti'> {
  form: UseFormReturn<FormData>
  value: FormData['roles']
  onChange: (roles: FormData['roles']) => void
  showSecondaryRoles?: boolean
}

interface StyledOptionContainerProps {
  isSelected?: boolean
}

const StyledOptionContainer = styled.div((props: StyledOptionContainerProps) => ({
  padding: spacing.X12,
  cursor: 'pointer',
  ':hover': {
    background: colors.GRAYSCALE.X20,
  },
  background: props.isSelected ? colors.GRAYSCALE.X20 : '',
  color: props.isSelected ? colors.GRAYSCALE.X50 : '',
  borderBottom: `1px solid ${colors.GRAYSCALE.X20}`,
}))

const StyledName = styled.div({
  fontSize: fontSize.X15,
  lineHeight: defaultLineHeight,
  fontWeight: 'bold',
  color: colors.GRAYSCALE.X70,
  marginBottom: spacing.X4,
})

const StyledDescription = styled.div({
  fontSize: fontSize.X12,
  lineHeight: defaultLineHeight,
  color: colors.GRAYSCALE.X50,
})

export const RoleCheckboxWrapper = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  margin: `${spacing.X12} 0`,
  fontSize: fontSize.X12,
  color: colors.GRAYSCALE.X70,

  '& label:first-of-type': {
    marginRight: spacing.X8,
  },
})

const Option: React.FunctionComponent<OptionProps<OptionType>> = props => (
  <StyledOptionContainer {...props.innerProps} isSelected={props.isSelected}>
    <StyledName>{props.data.label}</StyledName>
    <StyledDescription>{props.data.description}</StyledDescription>
  </StyledOptionContainer>
)

interface UseAccountManagementRoleSelectArgs {
  value: FormData['roles']
  primaryOptions: FormData['roles']
  onChange: (roles: FormData['roles']) => void
}

export const useAccountManagementRoleSelect = ({
  onChange,
  value,
  primaryOptions,
}: UseAccountManagementRoleSelectArgs) => {
  const [primaryRoleValues, setPrimaryRoleValues] = useState([])
  const [secondaryRoleValues, setSecondaryRoleValues] = useState([])

  useEffect(() => {
    const [primarySelected, secondarySelected] = partition(value, val =>
      primaryOptions.some(option => option.value == val.value)
    )

    setPrimaryRoleValues(primarySelected)
    setSecondaryRoleValues(secondarySelected)
  }, [value, primaryOptions])

  const onPrimaryRoleChange = selectValue => {
    let newValue

    if (Array.isArray(selectValue)) {
      newValue = selectValue
    } else if (selectValue === null) {
      newValue = []
    } else {
      newValue = [selectValue]
    }

    setPrimaryRoleValues(newValue)
    onChange(newValue.concat(secondaryRoleValues))
  }

  const onSecondaryRoleChange = (event, option) => {
    const { checked } = event.target
    let newValues

    if (checked) {
      newValues = [...secondaryRoleValues, option]
      setSecondaryRoleValues(newValues)
    } else {
      newValues = secondaryRoleValues.filter(value => value.value !== option.value)
      setSecondaryRoleValues(newValues)
    }

    onChange(primaryRoleValues.concat(newValues))
  }

  return {
    primaryRoleValues,
    setPrimaryRoleValues,
    secondaryRoleValues,
    setSecondaryRoleValues,
    onPrimaryRoleChange,
    onSecondaryRoleChange,
  }
}

export const AccountManagementRoleSelect: FunctionComponent<Props<OptionType>> = ({
  value,
  onChange,
  form,
  roleError = false,
  showSecondaryRoles = true,
  ...props
}) => {
  const formData = form.getValues()

  const { loading, error, primaryOptions, secondaryOptions } = useAccountManagementRoles({
    useDefaultOption: false,
    formData,
  })
  const messages = useDomainMessages({
    placeholder: 'accountManagementDomain.roles.placeholder',
    loading: 'accountManagementDomain.roles.loading',
    roles: 'accountManagementDomain.validation.roles',
  })

  const { primaryRoleValues, onPrimaryRoleChange, secondaryRoleValues, onSecondaryRoleChange } =
    useAccountManagementRoleSelect({ value, onChange, primaryOptions })

  return (
    <Fragment>
      <Select
        components={{ Option }}
        loading={loading}
        loadingMessage={messages.loading}
        placeholder={messages.placeholder}
        options={primaryOptions}
        error={error}
        {...props}
        isMulti={true}
        menuPlacement="top"
        value={primaryRoleValues}
        onChange={onPrimaryRoleChange}
        isClearable={false}
        // We need to show selected options so users can read descriptions of current roles.
        hideSelectedOptions={false}
        styles={{
          control: idsSelectStyles.control,
        }}
      />
      {roleError && (
        <ErrorMessage data-testid={'account-management-roles-select-error'}>
          {messages.roles}
        </ErrorMessage>
      )}

      {showSecondaryRoles &&
        secondaryOptions &&
        secondaryOptions.map(option => (
          <RoleCheckboxWrapper key={option.slug}>
            <Checkbox
              name={`${option.slug}_role`}
              id={`${option.slug}_role`}
              disabled={loading || option.isDisabled}
              checked={secondaryRoleValues.some(value => value.value === option.value)}
              onChange={event => onSecondaryRoleChange(event, option)}
            />
            <label htmlFor={`${option.slug}_role`}>{option.label}</label>
          </RoleCheckboxWrapper>
        ))}
    </Fragment>
  )
}
